import type { Characteristics, Consumption, OwccEnergyConsumptionLabelingData, VehicleTileData } from '@sccs/types';
import { pipe, rawTransform } from 'valibot';
import { UseCase, VissVehicleTileDataResponseWithId } from '../../http/clients/viss/VissClientTeaser';
import { TechnicalValue, TechnicalValuefootnote } from '../../types/viss/queries';
import { SchemaAny } from '../VehicleTileDataSchema';
import { generatePrices } from '../helper/prices';

export const vissTileDataTransfomer = pipe(
  SchemaAny,
  rawTransform<VissVehicleTileDataResponseWithId, VehicleTileData>(({ dataset }) => {
    const { market, lang, vehicle } = dataset.value;

    const technicalValueIdsCharacteristics = vehicle?.technicalValues?.useCases?.find(
      (useCase) => useCase?.id === UseCase.MAIN,
    );

    const technicalValueIdsConsumption = vehicle?.technicalValues?.useCases?.find(
      (useCase) => useCase?.id === UseCase.LEGAL,
    );

    const characteristics =
      vehicle?.technicalValues?.technicalValues
        ?.filter(
          (value) =>
            value?.id &&
            technicalValueIdsCharacteristics?.technicalValueIds?.includes(value?.id) &&
            value?.value &&
            value.value.trim() !== '-' &&
            value.label,
        )
        .map((technicalValue) =>
          mapTechnicalValueToCharacteristic(technicalValue, vehicle.technicalValues?.footnotes),
        ) ?? [];

    const consumption =
      vehicle?.technicalValues?.technicalValues
        ?.filter(
          (value) =>
            value?.id &&
            technicalValueIdsConsumption?.technicalValueIds?.includes(value?.id) &&
            value?.value &&
            value?.value.trim() !== '-' &&
            value.label,
        )
        .map((technicalValue) => mapTechnicalValueToConsumption(technicalValue, vehicle.technicalValues?.footnotes)) ??
      [];

    return {
      common: {
        hasCriticalError: vehicle?.hasCriticalVehicleTileError ?? true,
        identification: {
          vehicleId: vehicle?.vehicleId ?? undefined,
          productCode: vehicle?.productCode ?? undefined,
          configurationId: vehicle?.configurationId ?? undefined,
          commissionNumber: vehicle?.commissionNumber ?? undefined,
        },
        productType: !vehicle?.productType || vehicle?.productType === 'USED' ? 'STOCK' : vehicle?.productType,
        isVan: vehicle?.productCategory?.pls !== 'PC',
        bm6: vehicle?.baumuster ?? undefined,
        brand: vehicle?.brand ?? undefined,
        vehicleName: vehicle?.name ?? undefined,
      },
      tag: {
        badges: [
          {
            showIcon: true,
            badges:
              vehicle?.badges?.map((badge) => ({
                name: badge?.name ?? '',
                description: badge?.description ?? undefined,
                footnote: badge?.footnote ?? undefined,
              })) ?? [],
          },
        ],
      },
      image: {
        url: vehicle?.imageUrl ?? undefined,
        alt: vehicle?.name ?? undefined,
        title: vehicle?.name ?? undefined,
      },
      heading: {
        content: vehicle?.name ?? undefined,
      },
      characteristics,
      highlights: [],
      consumption,
      description: undefined,
      prices: generatePrices({
        market: market,
        locale: lang,
        productCategory: vehicle?.productCategory?.pls ?? undefined,
        productGroup: vehicle?.productCategory?.pls ?? undefined,
        changeYear: vehicle?.changeYear ?? undefined,
        productType: vehicle?.productType ?? undefined,
        productCode: vehicle?.productCode ?? undefined,
        commissionNumber: vehicle?.commissionNumber ?? undefined,
        configurationId: vehicle?.configurationId ?? undefined,
        vehicleId: vehicle?.vehicleId ?? undefined,
        baumuster: vehicle?.baumuster ?? undefined,
        modelYear: vehicle?.modelYear ?? undefined,
        nst: vehicle?.nst ?? undefined,
      }),
      ecoRating: {
        emission: (() => {
          const rawValue = (
            vehicle?.owccEnergyConsumptionLabelingData as OwccEnergyConsumptionLabelingData
          )?.energyConsumption.find(
            (consumption) => consumption?.id === 'technicalInformation.wltp.emission.total.primaryGear.individual',
          )?.rawValue;
          const emissionValue = Number(rawValue);
          if (!emissionValue) return;
          return emissionValue;
        })(),
      },
      envkv: vehicle?.owccEnergyConsumptionLabelingData ?? undefined,
      onlineCode: undefined,
      matchRate: undefined,
      cta: (() => {
        const href = vehicle?.links?.find((link) => link?.id === 'pdp')?.href;
        return href != null ? { pdp: href } : undefined;
      })(),
      availability: {
        status: (vehicle?.availability !== 'UNAVAILABLE' ? vehicle?.availability : undefined) ?? undefined,
        estimatedArrivalDate: vehicle?.estimatedArrivalDate ?? undefined,
      },
    };
  }),
);

const mapTechnicalValueToCharacteristic = (
  technicalValue?: TechnicalValue | null,
  footnotesDefinition?: Array<TechnicalValuefootnote | null> | null,
): Characteristics => {
  if (!technicalValue) {
    return {};
  }
  const { id, footnotes: footnoteKeys, value, workbenchIcon: icon } = technicalValue;
  return {
    id: id ?? undefined,
    footnotes: mapFootnotes(footnoteKeys, footnotesDefinition),
    value: value ?? undefined,
    icon: icon ?? undefined,
  };
};

const mapTechnicalValueToConsumption = (
  technicalValue?: TechnicalValue | null,
  footnotesDefinition?: Array<TechnicalValuefootnote | null> | null,
): Consumption => {
  if (!technicalValue) {
    return {};
  }
  const { id, label, footnotes: footnoteKeys, value } = technicalValue;

  return {
    id: id ?? undefined,
    footnotes: mapFootnotes(footnoteKeys, footnotesDefinition),
    value: value ?? undefined,
    label: label ?? undefined,
    isEnergyEfficiency:
      id?.endsWith('.energyEfficiencyClass') ||
      id?.endsWith('.co2Class.individual') ||
      id?.endsWith('.weightedCo2Class.individual') ||
      id?.endsWith('.energyEfficiencyCategory.individual') ||
      id?.endsWith('energyEfficiencyClass'),
  };
};

const mapFootnotes = (
  keys?: Array<string | null> | null,
  footnotesDefinition?: Array<TechnicalValuefootnote | null> | null,
): Array<string> | undefined => {
  return keys?.map(
    (key) =>
      footnotesDefinition?.find((footnoteDefinition) => footnoteDefinition && footnoteDefinition.footnote === key)
        ?.text ?? '',
  );
};
